import { z } from 'zod'
import { PlanLocationOverrideRuleStatus } from '../enums/plan-location-override-rule.enum'

// Outside the Zod schema — view-only, not editable.
export interface LocationViewMetadata {
  status: PlanLocationOverrideRuleStatus
  createdAt: string
  createdByEmail: string | null
  updatedAt: string
  updatedByEmail: string | null
}

export const US_STATES = [
  'AK',
  'AL',
  'AR',
  'AZ',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NC',
  'ND',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VA',
  'VI',
  'VT',
  'WA',
  'WI',
  'WV',
  'WY',
] as const

export type UsState = (typeof US_STATES)[number]

export const PlanLocationOverrideFormSchema = z.object({
  year: z
    .number({ required_error: 'Year is required' })
    .int()
    .min(2020, 'Year must be 2020 or later')
    .max(2035, 'Year must be 2035 or earlier'),
  state: z
    .string()
    .min(1, 'State is required')
    .refine((v) => US_STATES.includes(v as UsState), 'Invalid state code'),
  zipCode: z
    .string()
    .min(1, 'Zip Code is required')
    .regex(/^\d{5}$/, 'Zip Code must be exactly 5 digits'),
  countyFipsCode: z
    .string()
    .min(1, 'County FIPS Code is required')
    .regex(/^\d{5}$/, 'County FIPS Code must be exactly 5 digits'),
  county: z.string().min(1, 'County is required'),
  ratingArea: z.string().min(1, 'Rating Area is required'),
  sourceUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  notes: z
    .string()
    .max(1000, 'Notes must be 1000 characters or fewer')
    .optional(),
})

export type PlanLocationOverrideFormValues = z.infer<
  typeof PlanLocationOverrideFormSchema
>

export function flatToApiPayload(values: PlanLocationOverrideFormValues) {
  return {
    template: 'UPSERT_RULE' as const,
    match: {
      year: values.year,
      state: values.state,
      countyFipsCode: values.countyFipsCode,
      zipCode: values.zipCode,
      ratingArea: values.ratingArea,
    },
    params: {
      county: values.county,
    },
    ...(values.sourceUrl ? { sourceUrl: values.sourceUrl } : {}),
    ...(values.notes ? { notes: values.notes } : {}),
  }
}

export function apiPayloadToForm(rule: {
  match: {
    year: number
    state: string
    countyFipsCode: string
    zipCode: string
    ratingArea: string
  }
  params: { county: string }
  sourceUrl: string | null
  notes: string | null
}): PlanLocationOverrideFormValues {
  return {
    year: rule.match.year,
    state: rule.match.state,
    countyFipsCode: rule.match.countyFipsCode,
    zipCode: rule.match.zipCode,
    county: rule.params.county,
    ratingArea: rule.match.ratingArea,
    sourceUrl: rule.sourceUrl ?? '',
    notes: rule.notes ?? '',
  }
}
